<template>
  <BaseBlock title="Demandes finalisées">
    <v-container>
      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>
            Filtres de recherche
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row align="center">
              <v-col cols="2">
                <v-text-field
                  v-model="search.etablissement"
                  label="établissement"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model="search.demandeur"
                  label="demandeur"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-checkbox
                  v-model="search.accepte"
                  label="Agrément accepté"
                  hide-details
                ></v-checkbox>
              </v-col>
              <v-col cols="2">
                <v-checkbox
                  v-model="search.refuse"
                  label="Agrément refusé"
                  hide-details
                ></v-checkbox>
              </v-col>
            </v-row>
            <SearchDateWidget
              :searchOnDate="search.searchOnDate"
              v-on:update:searchOnDate="search.searchOnDate = $event"
              :dateMin="search.dateMin"
              v-on:update:dateMin="search.dateMin = $event"
              :dateMax="search.dateMax"
              v-on:update:dateMax="search.dateMax = $event"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-data-table
        :headers="headers"
        :items="dossiersFinalises"
        v-if="dossiers"
      >
        <!-- template du header "voir" -->
        <template v-slot:[`header.dossier`]="{}">
          <v-icon>mdi-eye</v-icon>
        </template>
        <!-- template des cellules "voir" -->
        <template v-slot:[`item.dossier`]="{ item }">
          <LinkToPDFWidget :dossier="item" />
        </template>
        <!-- template des cellules "établissement" -->
        <template v-slot:[`item.officine.denomination`]="{ item }">
          <div class="text-body-2">{{ item.officine.denomination }}</div>
          <div class="text-caption grey--text font-italic">
            {{ item.officine.codePostal }}
            -
            {{ item.officine.ville }}
          </div>
        </template>
        <!-- template des cellules "demandeur" -->
        <template v-slot:[`item.demandeur`]="{ item }">
          <div class="text-body-2">{{ item.demandeur }}</div>
          <div class="text-caption grey--text font-italic">
            <a
              target="_blank"
              :href="
                `mailto:${item.contactMail}?subject=Pharmagrément - Suivi du dossier de demande d'agrément de: ${item.officine.denomination}`
              "
            >
              {{ item.contactMail }}
            </a>
          </div>
        </template>
        <!-- template des cellules "date" -->
        <template v-slot:[`item.dateDepot`]="{ item }">
          <div class="text-body-2">{{ getDate(item.dateDepot) }}</div>
        </template>
        <!-- template des cellules "dateAvis" -->
        <template v-slot:[`item.dateAvis`]="{ item }">
          <div class="text-body-2">{{ getDate(item.dateDecisionCRA) }}</div>
        </template>
        <!-- template des cellules "avisCROP" -->
        <template v-slot:[`item.avisCROP`]="{ item }">
          <div class="text-body-2 text-center">
            {{
              item.avisCROP ? getAvisLibelle(item.avisCROP.avis) : "Pas d'avis"
            }}
          </div>
          <div>
            <v-btn
              v-if="item.avisCROP && item.avisCROP.avis !== null"
              @click="openVoirAvisCROPDialog(item)"
              style="float:right"
              x-small
            >
              Voir l'avis...
            </v-btn>
          </div>
        </template>
        <!-- template des cellules "avisRPO" -->
        <template v-slot:[`item.avisRPO`]="{ item }">
          <div class="text-body-2 text-center">
            {{
              item.avisRPO ? getAvisLibelle(item.avisRPO.avis) : "Pas d'avis"
            }}
          </div>
          <div>
            <v-btn
              v-if="item.avisRPO && item.avisRPO.avis !== null"
              @click="openVoirAvisRPODialog(item)"
              style="float:right"
              x-small
            >
              Voir l'avis...
            </v-btn>
          </div>
        </template>
        <!-- template des cellules "avisFinal" -->
        <template v-slot:[`item.etat.id`]="{ item }">
          <div v-if="item.etat.id === 5">
            Agrément validé <br />
            <div>
              <v-btn @click="revoquerAvis(item)" style="float:right" x-small>
                révoquer
              </v-btn>
            </div>
          </div>
          <div v-if="item.etat.id === 6">Agrément refusé</div>
        </template>
      </v-data-table>
      <v-btn @click="extractionCSV()" x-small>
        Extraction des informations (.csv)
      </v-btn>
    </v-container>
    <!-- popup voir avis du CROP -->
    <ModalVoirAvisCROP
      :avis="currentAvisCROP"
      :display="modalVoirAvisCROPDisplay"
      v-on:update:display="modalVoirAvisCROPDisplay = $event"
      v-if="modalVoirAvisCROPDisplay"
    />
    <!-- popup voir avis du RPO -->
    <ModalVoirAvisRPO
      :avis="currentAvisRPO"
      :display="modalVoirAvisRPODisplay"
      v-on:update:display="modalVoirAvisRPODisplay = $event"
      v-if="modalVoirAvisRPODisplay"
    />
  </BaseBlock>
</template>

<script>
import { mapState } from "vuex";
import common from "@/common";
import ApiAvisCROPRepository from "@/repositories/ApiAvisCROPRepository";
import ApiAvisRPORepository from "@/repositories/ApiAvisRPORepository";
import BaseBlock from "@/components/base/BaseBlock";
import LinkToPDFWidget from "@/components/LinkToPDFWidget";
import SearchDateWidget from "@/components/SearchDateWidget";
import ModalVoirAvisCROP from "@/components/ModalVoirAvisCROP";
import ModalVoirAvisRPO from "@/components/ModalVoirAvisRPO";

export default {
  name: "RecevabiliteDesDemandes",
  components: {
    BaseBlock,
    LinkToPDFWidget,
    ModalVoirAvisCROP,
    ModalVoirAvisRPO,
    SearchDateWidget
  },
  data() {
    return {
      refresh_dossier_timer: null,
      modalVoirAvisCROPDisplay: false,
      modalVoirAvisRPODisplay: false,
      currentAvisCROP: null,
      currentAvisRPO: null,
      search: {
        etablissement: "",
        demandeur: "",
        refuse: true,
        accepte: true,
        searchOnDate: false,
        dateMin: null,
        dateMax: null
      },
      headers: [
        {
          align: "center",
          sortable: false,
          value: "dossier"
        },
        {
          text: "Etablissement",
          align: "start",
          sortable: true,
          value: "officine.denomination"
        },
        {
          text: "Demandeur",
          align: "start",
          sortable: true,
          value: "demandeur"
        },
        {
          text: "Date de dépot de la demande",
          align: "start",
          sortable: true,
          value: "dateDepot"
        },
        {
          text: "Date de l'avis final",
          align: "start",
          sortable: true,
          value: "dateAvis"
        },
        {
          text: "Avis du CROP",
          align: "start",
          sortable: false,
          value: "avisCROP"
        },
        {
          text: "Avis du responsable de parcours",
          align: "start",
          filterable: false,
          sortable: true,
          value: "avisRPO"
        },
        {
          text: "Avis final",
          align: "start",
          sortable: false,
          value: "etat.id"
        }
      ]
    };
  },
  computed: {
    ...mapState(["dossiers", "user"]),
    dossiersFinalises() {
      return this.dossiers
        .filter(d => {
          if ("" !== this.search.etablissement) {
            if (
              !d.officine ||
              !d.officine.denomination
                .toLowerCase()
                .includes(this.search.etablissement)
            )
              return false;
          }
          if ("" !== this.search.demandeur) {
            if (!d.contactPrenom || !d.contactNom) return false;
            if (
              !(d.contactPrenom + " " + d.contactNom)
                .toLowerCase()
                .includes(this.search.demandeur)
            )
              return false;
          }
          // On ne veut pas voir les dossiers avec etat accepté
          if (false === this.search.accepte && common.isValide(d)) {
            return false;
          }
          // On ne veut pas voir les dossiers avec etat refusé
          if (false === this.search.refuse && common.isRefuse(d)) {
            return false;
          }
          if (
            this.search.searchOnDate &&
            !common.matchDate(d, this.search.dateMin, this.search.dateMax)
          )
            return false;
          return common.isValide(d) || common.isRefuse(d);
        })
        .map(d => {
          d.demandeur = d.contactPrenom + " " + d.contactNom;
          return d;
        });
    }
  },
  async created() {
    if (null === this.dossiers) await this.loadDossiers();
    else this.loadDossiers();
    this.refresh_dossier_timer = setInterval(
      this.loadDossiers,
      common.REFRESH_TIMER
    );
  },
  beforeDestroy() {
    clearInterval(this.refresh_dossier_timer);
  },
  methods: {
    async loadDossiers() {
      this.$store.dispatch("loadDossiers");
    },
    getDate(timestamp) {
      return common.formatDate(timestamp);
    },
    getAvisLibelle(avis) {
      return common.getAvisLibelle(avis);
    },
    envoyerMailInscription(dossier) {
      common.sendMailInscription(dossier);
      this.$toast.success("Le code a été renvoyé.");
    },
    async openVoirAvisCROPDialog(dossier) {
      if (
        !(
          this.currentAvisCROP &&
          dossier.avisCROP &&
          this.currentAvisCROP.id === dossier.avisCROP.id
        )
      )
        this.currentAvisCROP = await ApiAvisCROPRepository.get(
          dossier.avisCROP.id
        );
      this.modalVoirAvisCROPDisplay = true;
    },
    async openVoirAvisRPODialog(dossier) {
      if (
        !(
          this.currentAvisRPO &&
          dossier.avisRPO &&
          this.currentAvisRPO.id === dossier.avisRPO.id
        )
      )
        this.currentAvisRPO = await ApiAvisRPORepository.get(
          dossier.avisRPO.id
        );
      this.modalVoirAvisRPODisplay = true;
    },
    async revoquerAvis(dossier) {
      const confirmation = await this.$confirm(
        "<b>Voulez-vous révoquer cet agrément ?</b><br/><u>Cette action est irréversible.</u>"
      );
      if (confirmation) {
        await common.setEtatRefuse(dossier);
        this.$store.dispatch("loadDossiers");
      }
    },
    async extractionCSV() {
      await this.$store.dispatch("loadDossiers");
      const csv = common.getCSVDossiersFinalises(this.dossiers);
      const data = encodeURI(csv);
      const link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", "listeOfficineAgrements.csv");
      link.click();
    }
  }
};
</script>
